import _get from 'lodash/get'
import { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {  generateSubHeadingHtml, LOCATION_PERMISSION_STATUS } from './helper'
import { checkLocationAndShowLocationModal, hideLocationModal, setLocationPermissionStatus } from '../../store/actions/actions'
import { requestApiPost } from '../../api'
import { SET_USER_LOCATION } from '../../appConstants/APIEndpoint'
import useQueryParams from '../../hooks/useQueryParams'
import useRegisterTournament from '../../api/useRegisterTournament'

const useGeoLocationProvider = () => {
  const { locationPermissionStatus, locationModal, locationErrorObject } = useSelector(state => state.appReducer)
  const { gameName, ltid = 0 } = useQueryParams(["gameName", "ltid"]);
  const parsedLtid = Number(ltid);
  const data = { gameName: "LUDONX", ltid: parsedLtid };
  const { registerTournament, loading } = useRegisterTournament(data);

  const [subHeading, setSubHeading] = useState('')
  const [allowGps, setAllowGps] = useState(true)
  const [userFromBannedState, setUserFromBannedState] = useState(false)
  const [userState, setUserState] = useState('')
  const dispatch = useDispatch()

  useEffect(() => {
    setSubHeading(
      generateSubHeadingHtml(
        locationErrorObject?.error || locationErrorObject?.message,
        locationErrorObject?.state,
        locationErrorObject?.bannedStates,
        locationErrorObject?.gameNameTitle
      )
    )
    setAllowGps(locationErrorObject?.allowGPS)
  }, [
    locationErrorObject?.allowGPS,
    locationErrorObject?.gameNameTitle,
    locationErrorObject?.state,
    locationErrorObject?.error,
    locationErrorObject?.message,
    locationErrorObject?.bannedStates
  ])

  const giveLocationPermission = useCallback(async () => {
    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' })
      let authResponse = LOCATION_PERMISSION_STATUS.DENIED
      
      if (permission.state === 'granted') {
        authResponse = LOCATION_PERMISSION_STATUS.GRANTED
      } else if (permission.state === 'prompt') {
        authResponse = LOCATION_PERMISSION_STATUS.NOT_ASKED
      }
      dispatch(setLocationPermissionStatus(authResponse))
    } catch (error) {
      console.log('usegeolocation giveLocationPermission', {
        isLogToFile: false,
        isAddEvent: true,
        error
      })
      throw error
    }
  }, [])

  const storeAddressAtBackend = useCallback(locationObj => {
    navigator.geolocation.getCurrentPosition(
      async position => {
        dispatch(hideLocationModal())
        const lat = position.coords.latitude
        const lng = position.coords.longitude
        try {
          const body = {
            lat,
            lng,
            action: locationObj?.action,
            entryFee: locationObj?.entryFee,
            gameName: locationObj?.gameName
          }
          // showLoader(translations.gettingUserLocation)
          const data = await requestApiPost(SET_USER_LOCATION, body)
          const response = data?.response
          // hideLoader()
          if (response?.success) {
            const setUserLocationResponse = response?.data
            const locationObject = {
              state: setUserLocationResponse.state,
              message: setUserLocationResponse.message,
              allowGPS: true,
            }
            if(setUserLocationResponse?.available) {
              dispatch(setLocationPermissionStatus(LOCATION_PERMISSION_STATUS.GRANTED))
              registerTournament(true)
              setTimeout(() => {
                dispatch(hideLocationModal());
              }, 1000);
            } else {
              dispatch(setLocationPermissionStatus(LOCATION_PERMISSION_STATUS.RESTRICTED))
            }
            await checkLocationAndShowLocationModal({ ...locationObject, action: locationObj.action }, storeAddressAtBackend)
            setUserState(setUserLocationResponse.state.split('(')[0])
            setSubHeading(
              generateSubHeadingHtml(
                setUserLocationResponse.message,
                setUserLocationResponse.state,
                setUserLocationResponse.bannedStates,
                null,
                setUserLocationResponse.bannedGames
              )
            )
            setUserFromBannedState(!setUserLocationResponse.available)
          } else {
            console.log('usegeolocation storeAddressAtBackend', {
              isLogToFile: false,
              isAddEvent: true,
              data: response
            })
          }
        } catch (error) {
          console.log('usegeolocation storeAddressAtBackend', {
            isLogToFile: false,
            isAddEvent: true,
            error
          })
        }
      },
      (error) => {
        console.log('usegeolocation storeAddressAtBackend', {
          isLogToFile: false,
          isAddEvent: true,
          error
        })
        // Handle location access denial
        if (error.code === 1) { // 1 = PERMISSION_DENIED
          dispatch(setLocationPermissionStatus(LOCATION_PERMISSION_STATUS.DENIED))
          const locationObject = {
            state: '',
            message: error.message || 'Location Access Denied',
            allowGPS: false,
          }
          checkLocationAndShowLocationModal({ ...locationObject, action: locationObj?.action }, storeAddressAtBackend)
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    )
  }, [])

  const allowPermission = useCallback(
    async locationObj => {
      if (locationPermissionStatus === LOCATION_PERMISSION_STATUS.NOT_ASKED) {
        await giveLocationPermission()
        await storeAddressAtBackend(locationObj)
      }
    },
    [giveLocationPermission, locationPermissionStatus, storeAddressAtBackend]
  )

  const denyPermission = () => {
    dispatch(hideLocationModal())
  }

  const handleCloseBottomSheet = useCallback(() => {
    dispatch(hideLocationModal())
    dispatch(setLocationPermissionStatus(LOCATION_PERMISSION_STATUS.NOT_ASKED))

    // clear and set state name for home page API
    // if (locationPermissionStatus === LOCATION_PERMISSION_STATUS.GRANTED) {
    //   // clearHomePageResponseFromStore()
    //   dispatch(setLocationState(userState))
    // }
  }, [dispatch])

  const openSettings = useCallback(() => {
    dispatch(hideLocationModal())
    dispatch(setLocationPermissionStatus(LOCATION_PERMISSION_STATUS.NOT_ASKED))
    
    // Detect browser/platform
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1
    const isEdge = navigator.userAgent.toLowerCase().indexOf('edg') > -1

    // Open appropriate settings URL based on browser/platform
    if (isIOS) {
      window.open('App-Prefs:Location') // iOS settings
    } else if (isFirefox) {
      window.open('about:preferences#privacy') // Firefox settings
    } else if (isSafari) {
      window.open('safari://settings') // Safari settings
    } else if (isEdge) {
      window.open('edge://settings/content/location') // Edge settings
    } else {
      window.open('chrome://settings');  // This will show "not allowed" in console but is harmless
      alert('Please enable location permissions in your browser settings to continue.'); // Chrome settings (default)
    }
  }, [])

  return {
    handleCloseBottomSheet,
    openSettings,
    denyPermission,
    allowPermission,
    locationModal,
    locationPermissionStatus,
    userFromBannedState,
    subHeading,
    allowGps,
    locationErrorObject
  }
}

export default useGeoLocationProvider
